import { Environment } from "@omni/types/enums/environment.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { hasWindow } from "@omni/utils/dom.utils";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";

import * as WindowManagement from "@/services/windowManagement";
import { WindowKeys } from "@/services/windowManagement";
import { Config } from "@/utils/config.utils";

import { FeatureToggleChangeEvent } from "../windowEvents/events/featureToggleChange.event";
import { FeatureToggleStorageEntry as FeatureToggleEntry } from "./featureToggles.types";

const FEATURE_TOGGLE_PREFIX = "hubo.feature.";

// Only client side toggles
// Default values of server side toggles are managed through OSGI configuration
const defaultFeatureToggles: Partial<Record<FeatureToggleKey, boolean>> = {
    [FeatureToggleKey.DEBUG_ANALYTICS_LOGGING]: false,
    [FeatureToggleKey.DEBUG_EVENT_DATA_LAYER_LOGGING]: false,
    [FeatureToggleKey.DEBUG_ALGOLIA]: false,
    [FeatureToggleKey.DEBUG_FEATURE_SWITCH_UI]: false,
    [FeatureToggleKey.FEATURE_CART_ITEM_PROMOTION_LABEL]: false,
    [FeatureToggleKey.FEATURE_WISHLIST_BADGE]: false,
    [FeatureToggleKey.FEATURE_AUTHORISATION_MODE]: false,
    [FeatureToggleKey.FEATURE_STICKY_ECOMMERCE]: false
};

const ServerSideFeatureToggleKeys: Array<FeatureToggleKey> = [
    FeatureToggleKey.FEATURE_NEVERBOUNCE,
    FeatureToggleKey.FEATURE_REVIEWS,
    FeatureToggleKey.FEATURE_CONTACTFORM_ATTACHMENTS,
    FeatureToggleKey.FEATURE_KLARNA_OSM
];

export default class FeatureTogglesService {
    public static isEnabled(
        name: FeatureToggleKey,
        options?: {
            envOverrides?: Array<Environment>;
            serverFeatureToggles?: Partial<Record<FeatureToggleKey, boolean>>;
        }
    ) {
        const envOverrides = options?.envOverrides ?? [];
        const serverFeatureToggles = options?.serverFeatureToggles ?? {};

        if (envOverrides.some((env) => env === Config.general.environment))
            return true;

        const localeStorageFeature =
            FeatureTogglesService.getLocalstorageFeature(name);

        if (isDefined(localeStorageFeature)) {
            return localeStorageFeature;
        }

        if (FeatureTogglesService.isServerSide(name)) {
            return FeatureTogglesService.getServerFeature(
                name,
                serverFeatureToggles
            );
        } else {
            return FeatureTogglesService.getClientFeature(name);
        }
    }

    public static setFeature(
        name: FeatureToggleKey,
        enabled: boolean = false
    ): void {
        const featureToggleEntry: FeatureToggleEntry = {
            name: name,
            enabled: enabled,
            dateChanged: Date.now()
        };

        LocalStorage.setItem(
            FEATURE_TOGGLE_PREFIX + name,
            JSON.stringify(featureToggleEntry)
        );

        window.dispatchEvent(new FeatureToggleChangeEvent(name, enabled));
    }

    public static isServerSide(name: FeatureToggleKey) {
        return ServerSideFeatureToggleKeys.includes(name);
    }

    private static getLocalstorageFeature(name: FeatureToggleKey) {
        if (!hasWindow()) return null;

        const featureString = LocalStorage.getItem(
            FEATURE_TOGGLE_PREFIX + name
        );

        const feature = featureString
            ? attempt<FeatureToggleEntry | null>(
                  () => JSON.parse(featureString),
                  null
              )
            : null;

        return feature?.enabled ?? null;
    }

    private static getServerFeature(
        name: FeatureToggleKey,
        serverFeatureToggles: Partial<Record<FeatureToggleKey, boolean>> = {}
    ) {
        if (hasWindow()) {
            return Config["feature-toggles"][name] ?? null;
        } else if (isDefined(serverFeatureToggles[name])) {
            return serverFeatureToggles[name] ?? null;
        } else {
            return null;
        }
    }

    private static getClientFeature(name: FeatureToggleKey) {
        return defaultFeatureToggles[name] ?? null;
    }
}

const init = () => {
    WindowManagement.saveObject(
        WindowKeys.FEATURE_TOGGLES,
        FeatureTogglesService
    );
};

export { init };
